import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PaymentMethodButton from '../payment-methods/PaymentMethodButton';
import ModalWithHeader from '../../../components/modal-with-header/ModalWithHeader';

import Error from '../../../components/error/Error';
import { trackEvent, trackPixelEvent } from '../../../support-and-tracking/analytics-actions';
import { getFormActions } from '../../../utils/form-helpers';
import formConfig from '../../../payment/vipps-form-config';
import RoundedButtonAction from '../../../components/button/rounded-button-action/RoundedButtonAction';
import Spinner from '../../../components/spinner/Spinner';
import {
    getGiftCardForPayment,
    getGreetingCardForPayment,
    getVippsEnabled,
    getVippsForm,
    getVippsGiveMaxAmount,
} from '../../../payment/payment-selectors';
import { VIPPS_PAYMENT_IS_DISABLED } from '../../../constants/features/constants';
import { useProject } from '../../project-hooks/useProject';
import { useSubproject } from '../../../components/hooks/useSubproject';
import { useSearchParams } from 'react-router-dom';
import { projectIsFundAsGiftProject } from '../../fund-as-gift/fund-as-gift-model';
import styles from './vipps.module.less';
import { usePayment } from '../../../components/hooks/usePayment';
import { VippsLogo } from '../../../components/button/vipps-button/VippsLogo';
import ScreenReaderOnly from '../../../accessibility/ScreenReaderOnly';
import { initiateVippsPayment, initiateVippsPaymentWithoutProject } from '../../../payment/vipps-actions';

const { clearAll } = getFormActions(formConfig);

const Vipps = ({ isCart = false, amount, rewards, disabled = false, giverName, comment }) => {
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();
    const projectPollOptionIdString = searchParams.get('projectPollOptionId');
    const projectPollOptionId = projectPollOptionIdString ? parseInt(projectPollOptionIdString, 10) : undefined;
    const project = useProject();
    const payment = usePayment();
    const { subproject } = useSubproject();
    const vippsForm = useSelector((state) => getVippsForm(state));
    const vippsEnabled = useSelector((state) => getVippsEnabled(state));
    const maxAmount = useSelector((state) => getVippsGiveMaxAmount(state));
    const vippsCurrentlyUnavailable = useSelector(
        (state) => state.features.vippsCurrentlyUnavailable || state.features[VIPPS_PAYMENT_IS_DISABLED]
    );
    const greetingCard = useSelector((state) => getGreetingCardForPayment(state, project?.id));
    const giftCard = useSelector((state) => getGiftCardForPayment(state));
    useEffect(() => {
        return () => {
            dispatch(clearAll());
        };
    }, []);

    const buttonClicked = () => {
        trackEvent('support', 'checkout-provider', {
            method: 'Vipps',
            'project-id': project?.id,
            'subproject-id': subproject?.id,
            amount,
            cart: isCart,
            reward: rewards?.length > 0,
            giverName: !!giverName,
            comment: !!comment,
        });
        if (projectIsFundAsGiftProject(project)) {
            trackEvent('payment-wizard', 'checkout-provider', {
                method: 'Vipps',
                'project-id': project?.id,
                'fund-as-gift-code': project?.profile?.fundAsGiftCode,
                amount,
            });
        }
        startVipps();
    };

    const closeErrorModal = () => {
        dispatch(clearAll());
    };

    const startVipps = () => {
        trackPixelEvent('InitiateCheckout');

        const initiateVippsData = {
            projectId: project?.id,
            subprojectId: subproject?.id,
            projectPollOptionId,
            amount,
            rewards,
            nameAndComment: {
                giverName,
                comment,
                anonymous: giverName ? false : undefined,
            },
            greetingCard,
            giftCard,
        };
        trackEvent('support', 'checkout-pay', {
            method: 'Vipps',
            'project-id': project?.id,
            'subproject-id': subproject?.id,
            amount: initiateVippsData.amount,
            cart: isCart,
            reward: rewards?.length > 0,
        });
        if (projectIsFundAsGiftProject(project)) {
            trackEvent('payment-wizard', 'checkout-pay', {
                method: 'Vipps',
                'project-id': project.id,
                'fund-as-gift-code': project?.profile?.fundAsGiftCode,
                amount,
            });
        }
        if (payment.requiresProject === false) {
            dispatch(initiateVippsPaymentWithoutProject(initiateVippsData));
        } else {
            dispatch(initiateVippsPayment(initiateVippsData));
        }
    };

    const { errorModalIsOpen, errors, isInitiatingPayment } = vippsForm;

    if (!vippsEnabled || (payment.requiresProject === true && !project)) {
        return null;
    }

    const amountIsGreaterThanVippsMax = amount > maxAmount;

    const useVippsBranding = payment.useVippsBranding;
    const buttonText = useVippsBranding ? (
        <>
            Betal med <ScreenReaderOnly>Vipps</ScreenReaderOnly> <VippsLogo />
        </>
    ) : (
        'Vipps'
    );

    return (
        <div>
            <PaymentMethodButton
                type="button"
                onClick={buttonClicked}
                disabled={disabled || isInitiatingPayment || amountIsGreaterThanVippsMax || vippsCurrentlyUnavailable}
                className={useVippsBranding ? styles.vippsPaymentButton : styles.button}
            >
                {buttonText} {isInitiatingPayment && <Spinner className={styles.spinner} />}
            </PaymentMethodButton>

            <ModalWithHeader
                isOpen={errorModalIsOpen}
                title="Noe gikk galt med innbetalingen"
                onRequestClose={() => closeErrorModal()}
            >
                <div className={styles.errorModalMessage}>
                    <Error error={errors.global} />
                </div>
                <RoundedButtonAction className={styles.button} onClick={() => closeErrorModal()}>
                    Lukk
                </RoundedButtonAction>
            </ModalWithHeader>
        </div>
    );
};

export default Vipps;
